import type { SxProps, Theme } from '@mui/material';
import { Box, Divider, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { Fragment } from 'react';

export type FooterProps = {
  /** Organisation name, rendered after "© {year}". */
  copyright: ReactNode;
  /** Small muted text after the copyright, such as version info. */
  meta?: ReactNode;
  /** Left-column links, separated by a dot. */
  links?: ReactNode[];
  rightTitle?: ReactNode;
  rightSubtitle?: ReactNode;
  sx?: SxProps<Theme>;
};

/** Page footer: copyright and links on the left, contact details on the right. */
export function Footer({
  copyright,
  meta,
  links = [],
  rightTitle,
  rightSubtitle,
  sx,
}: FooterProps) {
  return (
    <Box component="footer" sx={sx}>
      <Divider sx={{ mt: 6 }} />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2, md: 0 },
          width: '100%',
          py: 4,
        }}
      >
        <Box sx={{ order: { xs: 1, md: 0 } }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: 'text.secondary',
              textAlign: { xs: 'center', md: 'left' },
            }}
          >
            © {new Date().getFullYear()} {copyright}
            {meta && (
              <Typography
                component="span"
                variant="body3"
                sx={{ color: 'text.disabled', ml: 1 }}
              >
                {meta}
              </Typography>
            )}
          </Typography>
          {links.length > 0 && (
            // A div, since link slots may render dialogs, which can't sit in a <p>.
            <Typography
              component="div"
              variant="body2"
              sx={{
                color: 'text.secondary',
                mt: 0.5,
                textAlign: { xs: 'center', md: 'left' },
                '> a, > button': {
                  color: 'text.secondary',
                  textDecoration: 'none',
                  font: 'inherit',
                },
              }}
            >
              {links.map((link, index) => (
                <Fragment key={index}>
                  {index > 0 && '・'}
                  {link}
                </Fragment>
              ))}
            </Typography>
          )}
        </Box>
        <Box>
          {rightTitle && (
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                color: 'text.secondary',
                textAlign: { xs: 'center', md: 'right' },
              }}
            >
              {rightTitle}
            </Typography>
          )}
          {rightSubtitle && (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mt: 0.5,
                textAlign: { xs: 'center', md: 'right' },
              }}
            >
              {rightSubtitle}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}
